import { RADAR_SIZE, RINGS, type RadarDot } from '../../lib/shadow-radar';

const CENTRE = RADAR_SIZE / 2;

/**
 * The shadow-surface radar (UI direction C). Each dot is a proposal discovery made; the nearer
 * the centre, the more its hosts lead to. Red is danger — a secret behind it, or data the
 * internet can read — amber is unowned, slate is owned by an application. Dots are buttons:
 * choosing one shows that proposal beside the radar.
 */
export function ShadowRadar({
  dots,
  selected,
  onSelect,
}: {
  dots: readonly RadarDot[];
  selected: string | null;
  onSelect: (key: string) => void;
}) {
  return (
    <svg
      className="sr-radar"
      viewBox={`0 0 ${RADAR_SIZE} ${RADAR_SIZE}`}
      width="100%"
      role="group"
      aria-label="Shadow surface radar: proposals placed by what lies behind them"
    >
      <g aria-hidden="true">
        {(['EDGE', 'COMPUTE', 'DATA'] as const).map((ring) => (
          <circle key={ring} className="sr-ring" cx={CENTRE} cy={CENTRE} r={RINGS[ring][1]} />
        ))}
        <line className="sr-axis" x1={CENTRE} y1={8} x2={CENTRE} y2={RADAR_SIZE - 8} />
        <line className="sr-axis" x1={8} y1={CENTRE} x2={RADAR_SIZE - 8} y2={CENTRE} />
        <text className="sr-ring-label" x={CENTRE + 4} y={CENTRE - RINGS.DATA[1] + 12}>
          data · secrets
        </text>
        <text className="sr-ring-label" x={CENTRE + 4} y={CENTRE - RINGS.COMPUTE[1] + 12}>
          compute
        </text>
        <text className="sr-ring-label" x={CENTRE + 4} y={CENTRE - RINGS.EDGE[1] + 12}>
          edge only
        </text>
      </g>
      {dots.map((d) => (
        <g
          key={d.key}
          className={`sr-dot sr-${d.tone}${selected === d.key ? ' is-selected' : ''}`}
          role="button"
          tabIndex={0}
          aria-pressed={selected === d.key}
          aria-label={`${d.name}: ${d.why}`}
          onClick={() => onSelect(d.key)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(d.key);
            }
          }}
        >
          {selected === d.key ? <circle className="sr-halo" cx={d.x} cy={d.y} r={d.r + 6} /> : null}
          <circle cx={d.x} cy={d.y} r={d.r} />
          {d.tone !== 'owned' ? (
            <text
              className="sr-label"
              x={d.x + (d.x >= CENTRE ? d.r + 4 : -(d.r + 4))}
              y={d.y + 4}
              textAnchor={d.x >= CENTRE ? 'start' : 'end'}
            >
              {d.name.length > 22 ? `${d.name.slice(0, 21)}…` : d.name}
            </text>
          ) : null}
        </g>
      ))}
    </svg>
  );
}
